import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Guest, AttendanceStatus, Invitation, InvitationType } from '../../_graphql/generated/types';
import { guestsApi, invitationsApi, accessLogsApi } from '../../_lib/api-client';
import BulkGuestImport from '../../_components/BulkGuestImport/BulkGuestImport';
import AddPlusOneModal from '../../_components/AddPlusOneModal/AddPlusOneModal';
import GuestDetailsModal from '../../_components/GuestDetailsModal/GuestDetailsModal';
import InvitationTypeSelectionModal from '../../_components/InvitationTypeSelectionModal/InvitationTypeSelectionModal';
import ConfirmationModal from '../../_components/ConfirmationModal/ConfirmationModal';
import UpgradeModal from '../../_components/UpgradeModal';

interface GuestListProps {
  eventId: string;
  eventCode?: string;
  invitationTypes?: InvitationType[];
  invitations?: Invitation[];
  onStatsUpdate?: (stats: {
    total: number;
    attending: number;
    notAttending: number;
    noResponse: number;
    notInvited: number;
    invited: number;
  }) => void;
}

type State =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'loaded'; guests: Guest[] };

const GuestList: React.FC<GuestListProps> = ({ eventId, eventCode, invitationTypes = [], invitations = [], onStatsUpdate }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [filter, setFilter] = useState<AttendanceStatus | 'ALL'>('ALL');
  const [invitationTypeFilter, setInvitationTypeFilter] = useState<string>('ALL');
  const [invitationFilter, setInvitationFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [newGuest, setNewGuest] = useState({ name: '', email: '', phone: '' });
  const [isAddingGuest, setIsAddingGuest] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string>('ALL');
  const [creatingInvitationForGuest, setCreatingInvitationForGuest] = useState<string | null>(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<string>>(new Set());
  const [isCreatingBulkInvitation, setIsCreatingBulkInvitation] = useState(false);
  const [hoveredInvitationId, setHoveredInvitationId] = useState<string | null>(null);
  const [copiedGuestId, setCopiedGuestId] = useState<string | null>(null);
  const [openMenuGuestId, setOpenMenuGuestId] = useState<string | null>(null);
  const [invitationTypeSelectionModal, setInvitationTypeSelectionModal] = useState<{
    isOpen: boolean;
    guestId: string;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
  } | null>(null);
  const [plusOneModalGuest, setPlusOneModalGuest] = useState<Guest | null>(null);
  const [detailsModalGuest, setDetailsModalGuest] = useState<Guest | null>(null);
  const [invitationViewCounts, setInvitationViewCounts] = useState<Record<string, number>>({});
  const [allGuestsStats, setAllGuestsStats] = useState<{
    total: number;
    attending: number;
    notAttending: number;
    noResponse: number;
    notInvited: number;
    invited: number;
  }>({ total: 0, attending: 0, notAttending: 0, noResponse: 0, notInvited: 0, invited: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isMountedRef = useRef(true);

  const fetchAllGuestsStats = async () => {
    try {
      const allGuests = await guestsApi.getByEvent(eventId);
      const stats = {
        total: allGuests.length,
        attending: allGuests.filter(g => g.invitationId && g.attendance === AttendanceStatus.ATTENDING).length,
        notAttending: allGuests.filter(g => g.invitationId && g.attendance === AttendanceStatus.NOT_ATTENDING).length,
        noResponse: allGuests.filter(g => g.invitationId && g.attendance === AttendanceStatus.NO_RESPONSE).length,
        notInvited: allGuests.filter(g => !g.invitationId).length,
        invited: 0
      };
      stats.invited = stats.total - stats.notInvited;
      setAllGuestsStats(stats);
    } catch (error) {
      console.error('Failed to fetch all guests stats:', error);
    }
  };

  const fetchGuests = async (isPolling = false) => {
    // Only show loading state on initial load, not during polling
    if (!isPolling) {
      setState({ status: 'loading' });
    }
    
    try {
      let guests: Guest[];
      
      // Priority: Invitation filter > Attendance filter > All guests
      if (invitationFilter !== 'ALL') {
        guests = await guestsApi.getByInvitation(invitationFilter);
      } else if (filter !== 'ALL') {
        guests = await guestsApi.getByEventAndStatus(eventId, filter as AttendanceStatus);
      } else {
        guests = await guestsApi.getByEvent(eventId);
      }
      
      // During polling, intelligently merge the data to preserve UI state
      if (isPolling && state.status === 'loaded') {
        // Create a map of existing guests for quick lookup
        const existingGuestsMap = new Map(state.guests.map(g => [g.id, g]));
        
        // Merge new data while preserving any local-only state
        const mergedGuests = guests.map(newGuest => {
          const existingGuest = existingGuestsMap.get(newGuest.id);
          if (existingGuest) {
            // Preserve local state that might not be reflected on server yet
            return {
              ...newGuest,
              // Preserve any fields that might be in transition
            };
          }
          return newGuest;
        });
        
        setState({ status: 'loaded', guests: mergedGuests });
      } else {
        setState({ status: 'loaded', guests });
      }
      
      // Fetch access logs to get view counts
      try {
        const accessLogs = await accessLogsApi.getByEvent(eventId);
        
        // Calculate view counts per invitation (excluding admin views)
        const viewCounts: Record<string, number> = {};
        accessLogs.forEach(log => {
          // Only count views where there's no userId (non-admin views)
          if (!log.userId && log.invitationId) {
            viewCounts[log.invitationId] = (viewCounts[log.invitationId] || 0) + 1;
          }
        });
        
        setInvitationViewCounts(viewCounts);
      } catch (error) {
        console.error('Failed to fetch access logs:', error);
        // Don't fail the whole operation if access logs fail
      }
    } catch (e) {
      // During polling, don't show error state - just log it
      if (isPolling) {
        console.error('Polling error:', e);
      } else {
        setState({ status: 'error', error: e instanceof Error ? e.message : 'Failed to load guests' });
      }
    }
  };

  useEffect(() => {
    fetchGuests();
  }, [eventId, filter, invitationFilter]);
  
  // Fetch all guests stats once on mount
  useEffect(() => {
    fetchAllGuestsStats();
  }, [eventId]);

  // Call the parent callback whenever stats change
  useEffect(() => {
    if (onStatsUpdate) {
      onStatsUpdate(allGuestsStats);
    }
  }, [allGuestsStats, onStatsUpdate]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!openMenuGuestId) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest('.guest-menu')) {
        setOpenMenuGuestId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuGuestId]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear any pending timeouts when component unmounts
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Polling for updates every 10 seconds
  useEffect(() => {
    // Don't poll if we're showing a modal or in multi-select mode
    const shouldPoll = !showBulkImport && !confirmModal?.isOpen && !plusOneModalGuest && 
                      !detailsModalGuest && !invitationTypeSelectionModal?.isOpen && 
                      !isMultiSelectMode && !showUpgradeModal;
    
    if (!shouldPoll) return;
    
    const pollInterval = setInterval(() => {
      // Only poll if the component is still mounted and we're not in the middle of an operation
      if (isMountedRef.current && !isAddingGuest && !creatingInvitationForGuest && !isCreatingBulkInvitation) {
        fetchGuests(true); // true indicates this is a polling request
        fetchAllGuestsStats(); // Also update stats during polling
      }
    }, 10000); // 10 seconds
    
    return () => clearInterval(pollInterval);
  }, [
    eventId, 
    filter, 
    invitationFilter, 
    showBulkImport, 
    confirmModal?.isOpen, 
    plusOneModalGuest, 
    detailsModalGuest, 
    invitationTypeSelectionModal?.isOpen, 
    isMultiSelectMode, 
    showUpgradeModal,
    isAddingGuest,
    creatingInvitationForGuest,
    isCreatingBulkInvitation
  ]);

  const handleAddGuest = async () => {
    if (!newGuest.name.trim()) {
      setAddError('Name is required');
      return;
    }

    setIsAddingGuest(true);
    setAddError(null);

    try {
      const createdGuest = await guestsApi.create({
        eventId,
        name: newGuest.name.trim(),
        email: newGuest.email.trim() || undefined,
        phone: newGuest.phone.trim() || undefined
      });
      
      // Update local state instead of refetching everything
      if (state.status === 'loaded' && createdGuest) {
        // Add the new guest to the existing list
        setState({ 
          status: 'loaded', 
          guests: [...state.guests, createdGuest] 
        });
        
        // Update stats
        setAllGuestsStats(prev => ({
          ...prev,
          total: prev.total + 1,
          notInvited: prev.notInvited + 1
        }));
      }
      
      setNewGuest({ name: '', email: '', phone: '' });
      
      // Focus the name input after successful addition
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to add guest');
    } finally {
      setIsAddingGuest(false);
    }
  };

  const handleNewGuestChange = (field: keyof typeof newGuest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewGuest(prev => ({ ...prev, [field]: e.target.value }));
    setAddError(null);
  };

  const handleCreateInvitation = async (guestId: string) => {
    // Check if there are any invitation types with designs
    const invitationTypesWithDesigns = invitationTypes.filter(type => 
      type.designCustomizations !== null && type.designCustomizations !== undefined
    );
    
    if (invitationTypesWithDesigns.length === 0) {
      setAddError('No invitation types with designs available. Please go to the Invitations section and design an invitation type first.');
      return;
    }

    // If there's only one invitation type with a design, use it automatically
    if (invitationTypesWithDesigns.length === 1) {
      await createInvitationWithType(guestId, invitationTypesWithDesigns[0].id);
    } else {
      // Show the selection modal if there are multiple types
      setInvitationTypeSelectionModal({
        isOpen: true,
        guestId
      });
    }
  };

  const createInvitationWithType = async (guestId: string, invitationTypeId: string) => {
    setCreatingInvitationForGuest(guestId);
    try {
      // Create the invitation with the guest included and the selected invitation type
      const invitation = await invitationsApi.create({
        eventId,
        invitationTypeId,
        allowedPlusOnes: 0, // Default to 0 plus ones
        guestIds: [guestId] // Include the guest in the invitation creation
      });

      // Update local state instead of refetching
      if (state.status === 'loaded' && invitation) {
        // Find the invitation type to get its details
        const invitationType = invitationTypes.find(it => it.id === invitationTypeId);
        
        // Update the guest with the new invitation
        const updatedGuests = state.guests.map(guest => {
          if (guest.id === guestId) {
            return {
              ...guest,
              invitationId: invitation.id,
              invitation: {
                ...invitation,
                invitationType
              }
            };
          }
          return guest;
        });
        
        setState({ status: 'loaded', guests: updatedGuests });
        
        // Update stats
        setAllGuestsStats(prev => ({
          ...prev,
          notInvited: prev.notInvited - 1,
          invited: prev.invited + 1,
          noResponse: prev.noResponse + 1
        }));
      }
      
      // Close the modal if it's open
      setInvitationTypeSelectionModal(null);
    } catch (e: any) {
      let errorMessage = 'Failed to create invitation';
      
      // graphql-request library wraps GraphQL errors in a specific format
      // The error object contains a response property with the GraphQL response
      if (e?.response?.errors?.[0]?.message) {
        errorMessage = e.response.errors[0].message;
      } else if (e?.message && typeof e.message === 'string') {
        // Sometimes the error message might be stringified JSON
        try {
          const parsed = JSON.parse(e.message);
          if (parsed?.response?.errors?.[0]?.message) {
            errorMessage = parsed.response.errors[0].message;
          }
        } catch {
          errorMessage = e.message;
        }
      }
      
      // Check if the error is related to guest limit
      if (errorMessage.includes('exceed the event guest limit')) {
        setShowUpgradeModal(true);
        setAddError(null); // Clear the error message when showing modal
      } else {
        setAddError(errorMessage);
      }
    } finally {
      setCreatingInvitationForGuest(null);
    }
  };

  const handleViewInvitation = (guest: Guest) => {
    if (guest.invitation?.token && eventCode) {
      // Get all guests with the same invitation
      const guestsWithSameInvitation = state.status === 'loaded' && state.guests
        ? state.guests.filter(g => g.invitationId === guest.invitationId)
        : [guest];
      
      // Create a URL-safe string of guest names
      const guestNames = guestsWithSameInvitation.length === 2
        ? guestsWithSameInvitation
            .map(g => g.name)
            .join(' and ')
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .trim()
        : guestsWithSameInvitation
            .map(g => g.name)
            .join(' ')
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .trim();
      
      // Open the public invitation view in a new tab with guest names
      const invitationUrl = `/invitations/${eventCode}/${guest.invitation.token}/${guestNames}`;
      window.open(invitationUrl, '_blank');
    } else if (!eventCode) {
      setAddError('Event code not available. Please refresh and try again.');
    } else {
      setAddError('Invitation token not available. Please refresh and try again.');
    }
  };

  const handleCopyInvitationLink = async (guest: Guest) => {
    if (guest.invitation?.token && eventCode) {
      try {
        // Get all guests with the same invitation
        const guestsWithSameInvitation = state.status === 'loaded' && state.guests
          ? state.guests.filter(g => g.invitationId === guest.invitationId)
          : [guest];
        
        // Create a URL-safe string of guest names
        const guestNames = guestsWithSameInvitation.length === 2
          ? guestsWithSameInvitation
              .map(g => g.name)
              .join(' and ')
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
              .replace(/\s+/g, '-') // Replace spaces with hyphens
              .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
              .trim()
          : guestsWithSameInvitation
              .map(g => g.name)
              .join(' ')
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
              .replace(/\s+/g, '-') // Replace spaces with hyphens
              .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
              .trim();
        
        // Get the full URL including the domain, event code, and guest names
        const invitationUrl = `${window.location.origin}/invitations/${eventCode}/${guest.invitation.token}/${guestNames}`;
        await navigator.clipboard.writeText(invitationUrl);
        
        // Show success state
        setCopiedGuestId(guest.id);
        // Clear any existing timeout
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }
        // Set new timeout
        copyTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setCopiedGuestId(null);
            copyTimeoutRef.current = null;
          }
        }, 2000);
      } catch (err) {
        setAddError('Failed to copy link to clipboard');
      }
    } else if (!eventCode) {
      setAddError('Event code not available. Please refresh and try again.');
    } else {
      setAddError('Invitation token not available. Please refresh and try again.');
    }
  };

  const handleDeleteGuest = (guestId: string, guestName?: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Guest',
      message: `Are you sure you want to delete ${guestName || 'this guest'}? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await guestsApi.delete(guestId);
          setOpenMenuGuestId(null);
          setConfirmModal(null);
          
          // Update local state instead of refetching
          if (state.status === 'loaded') {
            const deletedGuest = state.guests.find(g => g.id === guestId);
            setState({ 
              status: 'loaded', 
              guests: state.guests.filter(g => g.id !== guestId) 
            });
            
            // Update stats based on the deleted guest's status
            if (deletedGuest) {
              setAllGuestsStats(prev => {
                const newStats = { ...prev, total: prev.total - 1 };
                
                if (!deletedGuest.invitationId) {
                  newStats.notInvited = prev.notInvited - 1;
                } else {
                  newStats.invited = prev.invited - 1;
                  if (deletedGuest.attendance === AttendanceStatus.ATTENDING) {
                    newStats.attending = prev.attending - 1;
                  } else if (deletedGuest.attendance === AttendanceStatus.NOT_ATTENDING) {
                    newStats.notAttending = prev.notAttending - 1;
                  } else {
                    newStats.noResponse = prev.noResponse - 1;
                  }
                }
                
                return newStats;
              });
            }
          }
        } catch (e) {
          setAddError(e instanceof Error ? e.message : 'Failed to delete guest');
          setConfirmModal(null);
        }
      }
    });
  };

  const handleUninviteGuest = (guest: Guest) => {
    if (!guest.invitationId) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Uninvite Guest',
      message: `Are you sure you want to uninvite ${guest.name}? They will no longer have access to their invitation.`,
      confirmText: 'Uninvite',
      onConfirm: async () => {
        try {
          await guestsApi.update({
            id: guest.id,
            invitationId: null
          });
          setOpenMenuGuestId(null);
          setConfirmModal(null);
          
          // Update local state instead of refetching
          if (state.status === 'loaded') {
            const updatedGuests = state.guests.map(g => {
              if (g.id === guest.id) {
                return {
                  ...g,
                  invitationId: null,
                  invitation: undefined,
                  attendance: AttendanceStatus.NO_RESPONSE
                };
              }
              return g;
            });
            
            setState({ status: 'loaded', guests: updatedGuests });
            
            // Update stats
            setAllGuestsStats(prev => {
              const newStats = {
                ...prev,
                notInvited: prev.notInvited + 1,
                invited: prev.invited - 1
              };
              
              // Adjust attendance stats
              if (guest.attendance === AttendanceStatus.ATTENDING) {
                newStats.attending = prev.attending - 1;
              } else if (guest.attendance === AttendanceStatus.NOT_ATTENDING) {
                newStats.notAttending = prev.notAttending - 1;
              } else {
                newStats.noResponse = prev.noResponse - 1;
              }
              
              return newStats;
            });
          }
        } catch (e) {
          setAddError(e instanceof Error ? e.message : 'Failed to uninvite guest');
          setConfirmModal(null);
        }
      }
    });
  };

  const handleUpdateGuest = async (guestId: string, updates: { name?: string; email?: string; phone?: string }) => {
    await guestsApi.update({
      id: guestId,
      ...updates
    });
    
    // Update the state if the guest is still in view
    if (state.status === 'loaded') {
      const updatedGuests = state.guests.map(g => 
        g.id === guestId ? { ...g, ...updates } : g
      );
      setState({ status: 'loaded', guests: updatedGuests });
    }
    
    // Return the updated guest for the modal
    return { ...state.status === 'loaded' && state.guests.find(g => g.id === guestId), ...updates } as Guest;
  };

  if (state.status === 'loading') {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-rose-100">
        <div className="text-gray-600 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-400 mx-auto mb-4"></div>
          Loading guests...
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-red-100">
        <div className="text-red-600 text-center">Error: {state.error}</div>
      </div>
    );
  }

  const filteredGuests = state.status === 'loaded' && state.guests ? (() => {
    // First, filter the guests
    const filtered = state.guests.filter(guest => {
      // In multi-select mode, only show selected guests and uninvited guests
      if (isMultiSelectMode) {
        return selectedGuestIds.has(guest.id) || !guest.invitationId;
      }
      
      // Handle special quick filters
      if (activeQuickFilter === 'INVITED' && !guest.invitationId) {
        return false;
      }
      
      // For attendance status filters, ensure we only show invited guests
      if ((activeQuickFilter === 'ATTENDING_ONLY' || activeQuickFilter === 'NOT_ATTENDING' || activeQuickFilter === 'NO_RESPONSE') && !guest.invitationId) {
        return false;
      }
      
      // Server-side filtering is handled in fetchGuests, so we just apply client-side search and type filter
      const matchesTypeFilter = invitationTypeFilter === 'ALL' || 
        (invitationTypeFilter === 'UNASSIGNED' && !guest.invitationId) ||
        (invitationTypeFilter !== 'UNASSIGNED' && guest.invitation?.invitationType?.id === invitationTypeFilter);
      const matchesSearch = (guest.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                           (guest.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                           (guest.phone || '').includes(searchTerm);
      return matchesTypeFilter && matchesSearch;
    });

    // Then, group guests by invitation and plus ones with their primary guests
    const processedIds = new Set<string>();
    const grouped: Guest[] = [];
    
    // First pass: add all primary guests (guests without primaryGuestId)
    filtered.forEach(guest => {
      if (processedIds.has(guest.id) || guest.primaryGuestId) return;
      
      grouped.push(guest);
      processedIds.add(guest.id);
      
      // Add plus ones for this guest
      const plusOnes = filtered.filter(g => 
        g.primaryGuestId === guest.id && 
        !processedIds.has(g.id)
      );
      
      plusOnes.forEach(plusOne => {
        grouped.push(plusOne);
        processedIds.add(plusOne.id);
      });
      
      // If this guest has an invitation, find all other guests with the same invitation
      if (guest.invitationId) {
        const sameInvitationGuests = filtered.filter(g => 
          g.invitationId === guest.invitationId && 
          g.id !== guest.id && 
          !g.primaryGuestId && // Don't group plus ones here
          !processedIds.has(g.id)
        );
        
        sameInvitationGuests.forEach(g => {
          grouped.push(g);
          processedIds.add(g.id);
          
          // Add plus ones for each guest in the invitation group
          const guestPlusOnes = filtered.filter(po => 
            po.primaryGuestId === g.id && 
            !processedIds.has(po.id)
          );
          
          guestPlusOnes.forEach(plusOne => {
            grouped.push(plusOne);
            processedIds.add(plusOne.id);
          });
        });
      }
    });
    
    return grouped;
  })() : [];

  // Use the pre-fetched stats for button counts
  const attendanceStats = allGuestsStats;

  // Calculate invitation type stats
  const invitationTypeStats = invitationTypes.reduce((acc, type) => {
    const count = state.status === 'loaded' && state.guests 
      ? state.guests.filter(g => g.invitation?.invitationType?.id === type.id).length 
      : 0;
    acc[type.id] = { name: type.name, count };
    return acc;
  }, {} as Record<string, { name: string; count: number }>);

  const invitedGuests = allGuestsStats.invited;
  const responseRate = invitedGuests > 0 
    ? Math.round(((allGuestsStats.attending + allGuestsStats.notAttending) / invitedGuests) * 100)
    : 0;

  // Check if there are invitation types with designs available
  const hasInvitationTypesWithDesigns = invitationTypes.some(type => 
    type.designCustomizations !== null && type.designCustomizations !== undefined
  );

  const handleQuickFilter = (quickFilterType: string) => {
    setActiveQuickFilter(quickFilterType);
    switch (quickFilterType) {
      case 'ALL':
        setFilter('ALL');
        setInvitationTypeFilter('ALL');
        break;
      case 'NOT_INVITED':
        setFilter('ALL');
        setInvitationTypeFilter('UNASSIGNED');
        break;
      case 'INVITED':
        setFilter('ALL');
        setInvitationTypeFilter('ALL');
        // We'll handle the invited filter through the filteredGuests logic
        break;
      case 'ATTENDING_ONLY':
        setFilter(AttendanceStatus.ATTENDING);
        setInvitationTypeFilter('ALL');
        break;
      case 'NOT_ATTENDING':
        setFilter(AttendanceStatus.NOT_ATTENDING);
        setInvitationTypeFilter('ALL');
        break;
      case 'NO_RESPONSE':
        setFilter(AttendanceStatus.NO_RESPONSE);
        setInvitationTypeFilter('ALL');
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveQuickFilter('ALL');
  };

  const handleToggleGuestSelection = (guestId: string) => {
    const newSelected = new Set(selectedGuestIds);
    if (newSelected.has(guestId)) {
      newSelected.delete(guestId);
    } else {
      newSelected.add(guestId);
    }
    setSelectedGuestIds(newSelected);
  };

  const handleSelectAll = () => {
    const unassignedGuests = filteredGuests.filter(guest => !guest.invitationId);
    if (selectedGuestIds.size === unassignedGuests.length) {
      setSelectedGuestIds(new Set());
    } else {
      setSelectedGuestIds(new Set(unassignedGuests.map(g => g.id)));
    }
  };

  const handleCreateBulkInvitation = async () => {
    if (selectedGuestIds.size === 0) return;

    // Check if there are any invitation types with designs
    const invitationTypesWithDesigns = invitationTypes.filter(type => 
      type.designCustomizations !== null && type.designCustomizations !== undefined
    );
    
    if (invitationTypesWithDesigns.length === 0) {
      setAddError('No invitation types with designs available. Please go to the Invitations section and design an invitation type first.');
      return;
    }

    // If there's only one invitation type with a design, use it automatically
    if (invitationTypesWithDesigns.length === 1) {
      await createBulkInvitationWithType(invitationTypesWithDesigns[0].id);
    } else {
      // Show the selection modal if there are multiple types
      setInvitationTypeSelectionModal({
        isOpen: true,
        guestId: 'bulk' // Special case for bulk invitations
      });
    }
  };

  const createBulkInvitationWithType = async (invitationTypeId: string) => {
    setIsCreatingBulkInvitation(true);
    try {
      // Create the invitation with the selected guests
      const invitation = await invitationsApi.create({
        eventId,
        invitationTypeId,
        allowedPlusOnes: 0, // Default to 0 plus ones
        guestIds: Array.from(selectedGuestIds) // Include all selected guests
      });

      // Update local state instead of refetching
      if (state.status === 'loaded' && invitation) {
        // Find the invitation type to get its details
        const invitationType = invitationTypes.find(it => it.id === invitationTypeId);
        const selectedIds = Array.from(selectedGuestIds);
        
        // Update all selected guests with the new invitation
        const updatedGuests = state.guests.map(guest => {
          if (selectedIds.includes(guest.id)) {
            return {
              ...guest,
              invitationId: invitation.id,
              invitation: {
                ...invitation,
                invitationType
              }
            };
          }
          return guest;
        });
        
        setState({ status: 'loaded', guests: updatedGuests });
        
        // Update stats
        const guestCount = selectedIds.length;
        setAllGuestsStats(prev => ({
          ...prev,
          notInvited: prev.notInvited - guestCount,
          invited: prev.invited + guestCount,
          noResponse: prev.noResponse + guestCount
        }));
      }

      // Clear selection and exit multi-select mode
      setSelectedGuestIds(new Set());
      setIsMultiSelectMode(false);
      
      // Close the modal if it's open
      setInvitationTypeSelectionModal(null);
    } catch (e: any) {
      let errorMessage = 'Failed to create invitation';
      
      // graphql-request library wraps GraphQL errors in a specific format
      // The error object contains a response property with the GraphQL response
      if (e?.response?.errors?.[0]?.message) {
        errorMessage = e.response.errors[0].message;
      } else if (e?.message && typeof e.message === 'string') {
        // Sometimes the error message might be stringified JSON
        try {
          const parsed = JSON.parse(e.message);
          if (parsed?.response?.errors?.[0]?.message) {
            errorMessage = parsed.response.errors[0].message;
          }
        } catch {
          errorMessage = e.message;
        }
      }
      
      // Check if the error is related to guest limit
      if (errorMessage.includes('exceed the event guest limit')) {
        setShowUpgradeModal(true);
        setAddError(null); // Clear the error message when showing modal
      } else {
        setAddError(errorMessage);
      }
    } finally {
      setIsCreatingBulkInvitation(false);
    }
  };

  const handleExitMultiSelectMode = () => {
    setIsMultiSelectMode(false);
    setSelectedGuestIds(new Set());
  };

  const handleUpgrade = () => {
    // Navigate to upgrade page
    navigate('/app/billing/upgrade');
  };

  const hasActiveFilters = searchTerm !== '';

  // Count uninvited guests for determining if "Group with others" should be shown
  const uninvitedGuestsCount = filteredGuests.filter(g => !g.invitationId).length;

  return (
    <div className="space-y-6">
      {/* Compact Filter Panel */}
      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
        {/* Quick Filters with Counts and Bulk Import */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleQuickFilter('ALL')}
            disabled={isMultiSelectMode}
            className={`px-2.5 py-1 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 ${
              activeQuickFilter === 'ALL'
                ? 'bg-gray-900 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span>All</span>
            <span className={`font-bold ${
              activeQuickFilter === 'ALL' ? 'text-white' : 'text-gray-800'
            }`}>{attendanceStats.total}</span>
          </button>
          <button
            onClick={() => handleQuickFilter('NOT_INVITED')}
            disabled={isMultiSelectMode}
            className={`px-2.5 py-1 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 ${
              activeQuickFilter === 'NOT_INVITED'
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-orange-50 text-gray-700 hover:bg-orange-100 border border-orange-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span>Not Invited</span>
            <span className={`font-bold ${
              activeQuickFilter === 'NOT_INVITED' ? 'text-white' : 'text-orange-600'
            }`}>{attendanceStats.notInvited}</span>
          </button>
          <button
            onClick={() => handleQuickFilter('INVITED')}
            disabled={isMultiSelectMode}
            className={`px-2.5 py-1 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 ${
              activeQuickFilter === 'INVITED'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'bg-blue-50 text-gray-700 hover:bg-blue-100 border border-blue-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span>Invited</span>
            <span className={`font-bold ${
              activeQuickFilter === 'INVITED' ? 'text-white' : 'text-blue-600'
            }`}>{invitedGuests}</span>
          </button>
          <button
            onClick={() => handleQuickFilter('ATTENDING_ONLY')}
            disabled={isMultiSelectMode}
            className={`px-2.5 py-1 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 ${
              activeQuickFilter === 'ATTENDING_ONLY'
                ? 'bg-green-500 text-white shadow-sm'
                : 'bg-green-50 text-gray-700 hover:bg-green-100 border border-green-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span>Attending</span>
            <span className={`font-bold ${
              activeQuickFilter === 'ATTENDING_ONLY' ? 'text-white' : 'text-green-600'
            }`}>{attendanceStats.attending}</span>
          </button>
          <button
            onClick={() => handleQuickFilter('NOT_ATTENDING')}
            disabled={isMultiSelectMode}
            className={`px-2.5 py-1 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 ${
              activeQuickFilter === 'NOT_ATTENDING'
                ? 'bg-red-500 text-white shadow-sm'
                : 'bg-red-50 text-gray-700 hover:bg-red-100 border border-red-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span>Not Attending</span>
            <span className={`font-bold ${
              activeQuickFilter === 'NOT_ATTENDING' ? 'text-white' : 'text-red-600'
            }`}>{attendanceStats.notAttending}</span>
          </button>
          <button
            onClick={() => handleQuickFilter('NO_RESPONSE')}
            disabled={isMultiSelectMode}
            className={`px-2.5 py-1 text-xs sm:text-sm rounded-full transition-colors flex items-center gap-1.5 ${
              activeQuickFilter === 'NO_RESPONSE'
                ? 'bg-gray-400 text-white shadow-sm'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span>No Response</span>
            <span className={`font-bold ${
              activeQuickFilter === 'NO_RESPONSE' ? 'text-white' : 'text-gray-600'
            }`}>{attendanceStats.noResponse}</span>
          </button>
          
          <div className="flex-1" />
          
          <button
            onClick={() => setShowBulkImport(true)}
            className="px-3 py-1 bg-gray-900 text-white rounded-full hover:bg-gray-800 hover:shadow-md transition-all text-xs sm:text-sm font-medium"
          >
            Bulk Import
          </button>
        </div>

        {/* Search Bar and Results */}
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            placeholder="Search guests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isMultiSelectMode}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <div className="text-xs sm:text-sm text-gray-600 self-center">
            Showing {filteredGuests.length} of {allGuestsStats.total}
            {searchTerm && <span className="ml-1">• "{searchTerm}"</span>}
          </div>
        </div>
      </div>

      {/* Warning when no invitation types with designs */}
      {!hasInvitationTypesWithDesigns && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-amber-800">No Invitation Designs Available</h4>
              <p className="text-sm text-amber-700 mt-1 mb-3">
                Before creating invitations, you need to design at least one invitation type.
              </p>
              <button
                onClick={() => {
                  // Navigate to invitations tab
                  const currentEventCode = params.eventCode || eventCode;
                  const eventName = params.eventName || 'event';
                  navigate(`/app/events/${currentEventCode}/${eventName}?tab=invitations`);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Design First Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-select action bar */}
      {isMultiSelectMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              {selectedGuestIds.size === 0 ? (
                <p className="text-sm text-blue-700">Select guests to invite together</p>
              ) : (
                <p className="text-sm font-medium text-blue-900">
                  {selectedGuestIds.size} guest{selectedGuestIds.size !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreateBulkInvitation}
                disabled={selectedGuestIds.size === 0 || isCreatingBulkInvitation}
                className="px-4 py-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-white rounded-full hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
              >
                {isCreatingBulkInvitation ? 'Creating Invitation...' : 'Create Group Invitation'}
              </button>
              <button
                onClick={handleExitMultiSelectMode}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest list table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[400px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {isMultiSelectMode && (
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedGuestIds.size > 0 && selectedGuestIds.size === filteredGuests.filter(g => !g.invitationId).length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-300"
                    />
                  </th>
                )}
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Email</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Phone</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
          <tbody className="bg-white">
            {filteredGuests.map((guest, index) => {
              // Check if this guest is part of a group (has same invitationId as at least one other guest)
              const guestsWithSameInvitation = guest.invitationId ? 
                filteredGuests.filter(g => g.invitationId === guest.invitationId).length : 0;
              const isInGroup = guestsWithSameInvitation > 1;
              
              const isFirstInGroup = isInGroup && 
                (index === 0 || filteredGuests[index - 1]?.invitationId !== guest.invitationId);
              const isLastInGroup = isInGroup && 
                (index === filteredGuests.length - 1 || filteredGuests[index + 1]?.invitationId !== guest.invitationId);
              
              // Add border between rows (except within groups)
              const prevGuest = index > 0 ? filteredGuests[index - 1] : null;
              const shouldHaveDivider = index > 0 && (
                (!guest.invitationId && !prevGuest?.invitationId) || // Between non-invited guests
                (!guest.invitationId && prevGuest?.invitationId) || // Individual guest after a group
                (guest.invitationId && prevGuest && !prevGuest.invitationId) || // Group after individual guest
                (guest.invitationId && prevGuest?.invitationId && guest.invitationId !== prevGuest.invitationId) // Between different groups
              );
              
              // Create a data attribute for invitation groups
              const groupAttr = isInGroup && guest.invitationId ? { 'data-invitation-group': guest.invitationId } : {};
              
              // Check if this row should be highlighted
              const shouldHighlight = isInGroup && guest.invitationId === hoveredInvitationId;
              
              return (
              <tr 
                key={guest.id} 
                {...groupAttr}
                onMouseEnter={() => isInGroup && guest.invitationId && setHoveredInvitationId(guest.invitationId)}
                onMouseLeave={() => setHoveredInvitationId(null)}
                onClick={(e) => e.stopPropagation()}
                className={`transition-all duration-200 ${!isInGroup ? 'hover:bg-gray-50' : ''} ${isInGroup ? 'bg-blue-50/30' : ''} ${
                shouldHighlight ? 'bg-gray-100' : ''
              } ${
                shouldHaveDivider && !isFirstInGroup ? 'border-t border-gray-100' : ''
              } ${
                isFirstInGroup ? 'border-t-2 border-blue-200' : ''
              } ${
                isLastInGroup ? 'border-b-2 border-blue-200' : ''
              } ${
                isInGroup ? 'border-l-2 border-r-2 border-blue-200' : ''
              }`}>
                {isMultiSelectMode && (
                  <td className={`px-3 sm:px-4 py-3 ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedGuestIds.has(guest.id)}
                      onChange={() => handleToggleGuestSelection(guest.id)}
                      disabled={!!guest.invitationId}
                      className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-300 disabled:opacity-50"
                    />
                  </td>
                )}
                <td className={`px-3 sm:px-4 py-3 text-sm text-gray-500 ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>
                  {guest.guestNumber}
                </td>
                <td className={`px-3 sm:px-4 py-3 ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>
                  <div className="flex items-center gap-2">
                    {guest.primaryGuestId && (
                      <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
                        +1
                      </span>
                    )}
                    <div className="text-sm text-gray-900">
                      {guest.name || 'Guest'}
                    </div>
                  </div>
                  {isFirstInGroup && guest.invitation?.groupName && (
                    <div className="text-xs text-blue-600 font-medium mt-1">
                      Group: {guest.invitation.groupName}
                    </div>
                  )}
                  <div className="sm:hidden text-xs text-gray-500 mt-1">
                    {guest.email && <div>{guest.email}</div>}
                    {guest.phone && <div>{guest.phone}</div>}
                    {!guest.email && !guest.phone && <div>No contact info</div>}
                  </div>
                </td>
                <td className={`px-3 sm:px-4 py-3 text-sm text-gray-500 hidden sm:table-cell ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>{guest.email || '-'}</td>
                <td className={`px-3 sm:px-4 py-3 text-sm text-gray-500 hidden sm:table-cell ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>{guest.phone || '-'}</td>
                <td className={`px-3 sm:px-4 py-3 ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>
                  {/* Show status for ungrouped guests and specific attendance for grouped guests */}
                  {!guest.invitationId ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                      Not Invited
                    </span>
                  ) : (
                    <>
                      {/* For grouped guests, show "No Response" only on first, but always show actual attendance */}
                      {guest.attendance === AttendanceStatus.ATTENDING ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          Attending
                        </span>
                      ) : guest.attendance === AttendanceStatus.NOT_ATTENDING ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          Not Attending
                        </span>
                      ) : (
                        // Show "No Response" only for non-grouped guests or first in group
                        (!isInGroup || isFirstInGroup) && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                            No Response
                          </span>
                        )
                      )}
                    </>
                  )}
                </td>
                <td className={`px-3 sm:px-4 py-3 text-sm ${isFirstInGroup ? 'pt-4' : ''} ${isLastInGroup ? 'pb-4' : ''}`}>
                  {!guest.invitationId ? (
                    <div className="flex gap-1">
                      <button 
                        onClick={() => handleCreateInvitation(guest.id)}
                        disabled={creatingInvitationForGuest === guest.id || !hasInvitationTypesWithDesigns || isMultiSelectMode}
                        className="px-2 py-1 text-xs bg-gray-900 text-white rounded-full hover:bg-gray-800 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        title={!hasInvitationTypesWithDesigns ? "No invitation types with designs available" : isMultiSelectMode ? "Exit multi-select mode to invite individually" : "Create invitation for this guest"}
                      >
                        {creatingInvitationForGuest === guest.id 
                          ? 'Creating...' 
                          : !hasInvitationTypesWithDesigns 
                          ? 'No Designs' 
                          : 'Create invite link'}
                      </button>
                      {!isMultiSelectMode && uninvitedGuestsCount > 1 && (
                        <button 
                          onClick={() => {
                            setIsMultiSelectMode(true);
                            handleToggleGuestSelection(guest.id);
                          }}
                          disabled={!hasInvitationTypesWithDesigns}
                          className="px-2 py-1 text-xs bg-gradient-to-r from-purple-400 to-indigo-500 text-white rounded-full hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          title={!hasInvitationTypesWithDesigns ? "No invitation types with designs available" : "Select this guest and others to invite together"}
                        >
                          Group with others
                        </button>
                      )}
                      {/* Menu for uninvited guests */}
                      <div className="relative guest-menu">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenMenuGuestId(openMenuGuestId === guest.id ? null : guest.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                          title="More actions"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        
                        {openMenuGuestId === guest.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDetailsModalGuest(guest);
                                setOpenMenuGuestId(null);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg"
                            >
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>View Details</span>
                              </div>
                            </button>
                            {!guest.primaryGuestId && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPlusOneModalGuest(guest);
                                  setOpenMenuGuestId(null);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                  </svg>
                                  <span>Add Plus Ones</span>
                                </div>
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteGuest(guest.id, guest.name || 'this guest');
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg"
                            >
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Delete</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      {(!isInGroup || isFirstInGroup) && (
                        <>
                          <button 
                            onClick={() => handleViewInvitation(guest)}
                            className="px-2 py-1 text-xs bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-full hover:shadow-md transition-all"
                            title="View invitation as guest"
                          >
                            View
                          </button>
                          <button 
                            onClick={() => handleCopyInvitationLink(guest)}
                            className={`px-2 py-1 text-xs text-white rounded-full transition-all ${
                              copiedGuestId === guest.id 
                                ? 'bg-gradient-to-r from-emerald-400 to-teal-500 shadow-md' 
                                : 'bg-gradient-to-r from-gray-400 to-gray-600 hover:shadow-md'
                            }`}
                            title="Copy invitation link to clipboard"
                          >
                            {copiedGuestId === guest.id ? 'Copied!' : 'Copy link'}
                          </button>
                        </>
                      )}
                      <div className="relative guest-menu">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenMenuGuestId(openMenuGuestId === guest.id ? null : guest.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                          title="More actions"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        
                        {openMenuGuestId === guest.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDetailsModalGuest(guest);
                                setOpenMenuGuestId(null);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg"
                            >
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>View Details</span>
                              </div>
                            </button>
                            {guest.invitationId && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleUninviteGuest(guest);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                  <span>Uninvite</span>
                                </div>
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteGuest(guest.id, guest.name || 'this guest');
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg"
                            >
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Delete</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
            {/* Add new guest row */}
            <tr className="bg-gray-50">
              {isMultiSelectMode && <td className="px-3 sm:px-4 py-3"></td>}
              <td className="px-3 sm:px-4 py-3 text-sm text-gray-400">-</td>
              <td className="px-3 sm:px-4 py-3">
                <input
                  ref={nameInputRef}
                  type="text"
                  placeholder="New guest name"
                  value={newGuest.name}
                  onChange={handleNewGuestChange('name')}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddGuest()}
                  disabled={isAddingGuest}
                  className="w-full px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                />
              </td>
              <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={newGuest.email}
                  onChange={handleNewGuestChange('email')}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddGuest()}
                  disabled={isAddingGuest}
                  className="w-full px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                />
              </td>
              <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={newGuest.phone}
                  onChange={handleNewGuestChange('phone')}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddGuest()}
                  disabled={isAddingGuest}
                  className="w-full px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                />
              </td>
              <td className="px-3 sm:px-4 py-3">
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                  Not Invited
                </span>
              </td>
              <td className="px-3 sm:px-4 py-3">
                <button
                  onClick={handleAddGuest}
                  disabled={isAddingGuest}
                  className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  title="Add guest"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </td>
            </tr>
            {filteredGuests.length === 0 && !newGuest.name && (
              <tr>
                <td colSpan={isMultiSelectMode ? 7 : 6} className="px-3 sm:px-4 py-8 text-center text-sm text-gray-500">
                  {searchTerm || filter !== 'ALL' || invitationFilter !== 'ALL' ? 'No guests match your filters' : 'Start typing above to add your first guest'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        {addError && (
          <div className="px-3 sm:px-4 py-2 bg-red-50 border-t border-red-200">
            <p className="text-xs sm:text-sm text-red-600">{addError}</p>
          </div>
        )}
      </div>

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-bold mb-4">Bulk Import Guests</h3>
            <BulkGuestImport
              eventId={eventId}
              onSuccess={() => {
                setShowBulkImport(false);
                fetchGuests();
              }}
              onCancel={() => setShowBulkImport(false)}
            />
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-gray-900">{confirmModal.title}</h3>
            <p className="text-gray-600 mb-6">{confirmModal.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plus One Modal */}
      {plusOneModalGuest && (
        <AddPlusOneModal
          primaryGuest={plusOneModalGuest}
          eventId={eventId}
          invitationTypes={invitationTypes}
          onClose={() => setPlusOneModalGuest(null)}
          onSuccess={() => {
            setPlusOneModalGuest(null);
            fetchGuests();
          }}
        />
      )}

      {/* Guest Details Modal */}
      {detailsModalGuest && (
        <GuestDetailsModal
          guest={detailsModalGuest}
          eventCode={eventCode}
          hasInvitationTypesWithDesigns={hasInvitationTypesWithDesigns}
          onClose={() => setDetailsModalGuest(null)}
          onCreateInvitation={handleCreateInvitation}
          onViewInvitation={handleViewInvitation}
          onCopyInvitationLink={handleCopyInvitationLink}
          onAddPlusOne={(guest) => setPlusOneModalGuest(guest)}
          onUninvite={handleUninviteGuest}
          onDelete={handleDeleteGuest}
          onUpdateGuest={handleUpdateGuest}
          copiedGuestId={copiedGuestId}
          viewCount={detailsModalGuest.invitationId ? invitationViewCounts[detailsModalGuest.invitationId] || 0 : 0}
        />
      )}

      {/* Invitation Type Selection Modal */}
      {invitationTypeSelectionModal && (
        <InvitationTypeSelectionModal
          isOpen={invitationTypeSelectionModal.isOpen}
          invitationTypes={invitationTypes}
          onSelect={(invitationTypeId) => {
            if (invitationTypeSelectionModal.guestId === 'bulk') {
              createBulkInvitationWithType(invitationTypeId);
            } else {
              createInvitationWithType(invitationTypeSelectionModal.guestId, invitationTypeId);
            }
          }}
          onCancel={() => {
            setInvitationTypeSelectionModal(null);
            setCreatingInvitationForGuest(null);
            setIsCreatingBulkInvitation(false);
          }}
          isCreating={isCreatingBulkInvitation || creatingInvitationForGuest !== null}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
        currentGuestCount={allGuestsStats.total}
        maxFreeGuests={15}
        eventId={eventId}
      />
    </div>
  );
};

export default GuestList;